// Copyright (c) 2019 Uber Technologies, Inc.
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import React, { Component } from "react";
import { connect } from "react-redux";
import AutoSizer from "react-virtualized/dist/commonjs/AutoSizer";
import KeplerGl from "kepler.gl";
import { createAction } from "redux-actions";
import DashboardLayerUiContainer from "./components/dashboard-layer-ui-container";
import { dbColors } from "./constants/dbColors";
import KeplerGlSchema from "kepler.gl/schemas"; //NEW!
import Logo from "./img/SeeSense-Logo-Report-white.png";
import { addDataToMap, wrapTo } from "kepler.gl/actions";
import { processCsvData } from "kepler.gl/processors";
import { receiveMapConfig } from "kepler.gl/actions";
import { dbUiInfoSustrans, sustransInsightUiInfo } from "./constants/dbUiInfoSustrans";
import styled from "styled-components";

import {insights} from "Datafiles";
import baseConfig from "./configurations/reportDashboardConfig.json";

//the layer ID's from the config are stored in this object
const layerIds = {
  popularity: "exwolls39",
  speed: "58f4jx62d7",
  roughness: "f2euttsk",
  journeys: "l8igjoxh9",
  dwell: "l4adaufeb",
  combined: "exwolls39",
  sustransBikes: "msi9thb39"
};

const prsDataIds = [
  {layer: "closePass", id: "drt3g626e"},
  {layer: "collision", id: "1cssmv5f"},
  {layer: "pothole", id: "kiph27pn"},
  {layer: "obstruction", id: "8ezrc7yrb"},
  {layer: "rsother", id: "wtvcbz6y9"},
]

const irDataIds = [
  {layer: "width", id: "whc9sdmu"},
  {layer: "separation", id: "yxv40ttgl"},
  {layer: "parking", id: "ipppjv4rj"},
  {layer: "timing", id: "9w7783swp"},
  {layer: "calming", id: "moopy8lmq"},
  {layer: "closures", id: "h2j7xxtu5"},
  {layer: "irother", id: "xx4qnszwj"}
]

// LOCAL DEV
// const insights = {
//   prsDataFile: "http://localhost:4000/ride_surveys.csv",
//   irDataFile: "http://localhost:4000/report_dashboard_mock_data_infra_report.csv"
// };

//PRODUCTION
// const insights = {
//   prsDataFile: "",
//   irDataFile: ""
// };

const MAPBOX_TOKEN =
  "pk.eyJ1IjoiZXdhbmJlY2siLCJhIjoiY2pzZnk0Zmg1MDdlczN5bDVjbmt4MzFkbiJ9.FUXaom4P_JjmVBaHolfD0w"; // eslint-disable-line

//defintions for injecting custom theme colours into kepler, not used in report dashboard
const customTheme = {
  floatingBtnBgd: dbColors.highlight,
  floatingBtnBgdHover: "#295ED9",
  floatingBtnColor: dbColors.background,
  floatingBtnActColor: dbColors.background
}

class App extends Component {
  async componentDidMount() {

    const parsedConfig = KeplerGlSchema.parseSavedConfig(baseConfig);
    this.props.dispatch(receiveMapConfig(parsedConfig));

    const prsResponse = await fetch(insights.prsDataFile);
    const prsData = await prsResponse.text();
    const irResponse = await fetch(insights.irDataFile);
    const irData = await irResponse.text();

    const datasetsBuilder = arg => {
      let datasetHolder = [];
      arg.forEach((element) => {
        element.dataIds.forEach((layer) => {
          datasetHolder.push(
            {
              data: element.datafile,
              info: {id: layer.id}
            }
          )
        })
      })
      return datasetHolder;
    }

    this.props.dispatch(
      wrapTo(
        "map1",
        addDataToMap({
          datasets: datasetsBuilder(
            [
              {datafile: processCsvData(prsData), dataIds: prsDataIds},
              {datafile: processCsvData(irData), dataIds: irDataIds}
            ]
          ),
          options: {centerMap: false}
        })
      )
    );
  }

  render() {
    return (
      <div
        className={"wholePage"}
        id={"wholePage"}
        style={{ position: "absolute", width: "100%", height: "100%" }}
      >
        <div
          id={"topBar"}
          className={"topBar"}
          style={{
            position: "absolute",
            float: "left",
            clear: "none",
            zIndex: "2",
            width: "100%",
            height: "6%",
            backgroundColor: dbColors.black,
            minHeight: "3em"
          }}
        >
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              height: "100%"
            }}
          >
            <div
              className={"logoContainer"}
              style={{
                height: "100%",
                position: "relative",
                float: "left",
                clear: "none",
                marginLeft: "1.2em"
              }}
            >
              <img style={{ height: "100%" }} src={Logo} />
            </div>
          </div>
        </div>
        <div
          style={{
            top: "6%",
            height: "94%",
            width: "100%"
          }}
        >
          <AutoSizer
            style={{
              position: "absolute",
              float: "left",
              clear: "all",
              zIndex: 1
            }}
          >
            {({ height, width }) => (
              <KeplerGl
                mapboxApiAccessToken={MAPBOX_TOKEN}
                id="map1"
                width={width}
                height={height}
                theme={customTheme}
              />
            )}
          </AutoSizer>
        </div>
        <DashboardLayerUiContainer/>
      </div>
    );
  }
}

const mapStateToProps = state => state;
const dispatchToProps = dispatch => ({ dispatch });

export default connect(
  mapStateToProps,
  dispatchToProps
)(App);
